/* ------------------------------------------------------------------------------------------------------------------
   SIZE GUIDE
   Behaviour of the size guide drawer / popover (snippets/size-guide.liquid):

   - cm / inches switch on the reference measurements
   - size conversion table: hovering shades a column, clicking selects it (column shaded, clicked cell in
     black) and picks that size on the product form; the column follows the size selected on the page too
   - "Add to cart — Size X" button that submits the product form with the selected size, X being the size shown on
     the product page; when it was picked from another row of the table: "Size 38S (equivalent to US 4)"
   - clicking a size keeps only the clicked row and one reference row written like the product page ("38S");
     "Remove selected size" brings every row back
   - Klaviyo popups / flyouts stay hidden while a guide is open (html.is-size-guide-open, see custom.css)
   - opening a guide closes the mini cart, which would otherwise cover it

   On the look pages several guides share the page, one per product row (snippets/look-item.liquid): each row
   tags its size buttons with data-size-guide-for="<form id>", and a column click is announced to that form
   with a "size-guide:select" event so the row selects the size itself (rows have no option radios).
   ------------------------------------------------------------------------------------------------------------------ */

// The snippet, and so this script, is rendered once per guide: only the first copy runs
if (!window.customElements.get('size-guide')) {
  const CM_PER_INCH = 2.54;
  const CM_PATTERN = /(\d+(?:[.,]\d+)?)\s*cm\b/gi;
  const SIZE_PLACEHOLDER = '%size%';

  const SELECTORS = {
    cell: '[data-size-guide-col]',
    unitSwitch: '[data-size-guide-unit]',
    measurements: '[data-size-guide-measurements]',
    addButton: '[data-size-guide-add]',
    addLabel: '[data-size-guide-add-label]',
    row: '.size-guide__row',
    showAll: '[data-size-guide-show-all]',
    // One per size on the product page, with the variant id and its availability
    sizeButtons: '[data-submit-mobile][data-id]'
  };

  const OPEN_CLASS = 'is-size-guide-open';

  // Any guide open on the page (look pages have several) flags <html>
  const syncOpenClass = () => {
    const open = Array.from(document.querySelectorAll('size-guide'))
      .some((guide) => guide.closest('drawer-content, popover-content')?.hasAttribute('open'));

    document.documentElement.classList.toggle(OPEN_CLASS, open);
  };

  // The mini cart stays open while browsing and would cover the guide: it closes before the guide opens, so the guide's scroll lock stays
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[aria-controls]');
    const target = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
    const miniCart = document.getElementById('mini-cart');

    if (target?.querySelector('size-guide') && miniCart?.hasAttribute('open')) {
      miniCart.open = false;
    }
  }, true);

  const normalizeSize =(value) => String(value || '').toUpperCase().replace(/[\s/]/g, '');

  const cmToInches = (match, number) => {
    const inches = parseFloat(number.replace(',', '.')) / CM_PER_INCH;

    return `${inches.toFixed(1).replace('.', ',')} in`;
  };

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      // form.id would give the <input name="id"> of the product form, so the id string is kept aside
      this.formId = this.getAttribute('form-id');
      this.form = document.getElementById(this.formId);
      this.cells = Array.from(this.querySelectorAll(SELECTORS.cell));
      this.unitSwitch = this.querySelector(SELECTORS.unitSwitch);
      this.measurements = this.querySelector(SELECTORS.measurements);
      this.addButton = this.querySelector(SELECTORS.addButton);
      this.addLabel = this.querySelector(SELECTORS.addLabel);
      this.rows = Array.from(this.querySelectorAll(SELECTORS.row));
      this.showAll = this.querySelector(SELECTORS.showAll);
      this.selectedSize = null;
      this.equivalent = null;

      this.columns = this._readColumns();
      this.sizes = this._readSizes();

      if (this.measurements) {
        this.measurementsHtml = this.measurements.innerHTML;
      }

      this.unitSwitch?.addEventListener('change', () => this._renderUnits());

      this.cells.forEach((cell) => {
        const column = cell.dataset.sizeGuideCol;

        cell.addEventListener('pointerenter', () => this._toggleColumnClass(column, 'is-hover', true));
        cell.addEventListener('pointerleave', () => this._toggleColumnClass(column, 'is-hover', false));
        cell.addEventListener('click', () => this._onColumnClick(column, cell));
      });

      this.addButton?.addEventListener('click', () => this._addToCart());
      this.showAll?.addEventListener('click', () => this._showRows(null));

      if (this.form) {
        this.form.addEventListener('variant:changed', (event) => this._syncFromVariant(event.detail.variant));
        this.form.addEventListener('variant:added', () => this._close());
        this.form.addEventListener('cart-notification:show', () => this._setBusy(false));
      }

      this._markAvailableColumns();
      this._syncFromForm();
      this._watchOpen();
    }

    disconnectedCallback() {
      this.openObserver?.disconnect();
      syncOpenClass();
    }

    _watchOpen() {
      const container = this.closest('drawer-content, popover-content');

      if (!container) {
        return;
      }

      this.openObserver = new MutationObserver(syncOpenClass);
      this.openObserver.observe(container, { attributes: true, attributeFilter: ['open'] });
    }

    /* ---------- Data ---------- */

    // Columns are described on the first row cells: data-size-fr="38" data-size-int="S"
    _readColumns() {
      return this.cells
        .filter((cell) => cell.dataset.sizeFr)
        .map((cell) => ({
          column: cell.dataset.sizeGuideCol,
          fr: normalizeSize(cell.dataset.sizeFr),
          int: normalizeSize(cell.dataset.sizeInt)
        }));
    }

    _readSizes() {
      const ownButtons = document.querySelectorAll(`[data-size-guide-for="${this.formId}"]`);
      const buttons = ownButtons.length ? ownButtons : document.querySelectorAll(SELECTORS.sizeButtons);

      return Array.from(buttons).map((button) => ({
        value: button.querySelector('.value')?.textContent.trim() || '',
        variantId: button.dataset.id,
        available: button.dataset.available === 'true'
      }));
    }

    // "38S", "38 / S", "S" or "38" all point to the same column
    _columnForValue(value) {
      const normalized = normalizeSize(value);

      if (!normalized) {
        return null;
      }

      return this.columns.find(({ fr, int }) => normalized === fr + int || normalized === int || normalized === fr) || null;
    }

    _sizeForColumn(column) {
      return this.sizes.find((size) => this._columnForValue(size.value)?.column === column) || null;
    }

    _radioForSize(size) {
      const radios = document.querySelectorAll(`input[type="radio"][name^="option"][form="${this.formId}"]`);

      return Array.from(radios).find((radio) => radio.value === size.value) || null;
    }

    /* ---------- Rendering ---------- */

    _toggleColumnClass(column, className, force) {
      this.cells.forEach((cell) => {
        if (cell.dataset.sizeGuideCol === column) {
          cell.classList.toggle(className, force);
        }
      });
    }

    // Columns with no matching size on this product are not clickable; sold-out sizes are greyed like the size picker
    _markAvailableColumns() {
      this.columns.forEach(({ column }) => {
        const size = this._sizeForColumn(column);

        this._toggleColumnClass(column, 'is-selectable', size !== null);
        this._toggleColumnClass(column, 'is-sold-out', size !== null && !size.available);
      });
    }

    // The whole column is shaded; only one cell goes black: the clicked one, or the INT row by default
    _selectColumn(column, activeCell = null) {
      const active = activeCell
        || this.cells.find((cell) => cell.dataset.sizeGuideCol === column && cell.hasAttribute('data-size-guide-primary'));

      this.cells.forEach((cell) => {
        const inColumn = cell.dataset.sizeGuideCol === column;

        cell.classList.toggle('is-selected', inColumn);
        cell.classList.toggle('is-active', inColumn && cell === active);
      });
    }

    _renderAddButton() {
      if (!this.addButton || !this.addLabel) {
        return;
      }

      const { labelSize, labelEquivalent, labelPlain, labelSoldOut } = this.dataset;
      const size = this.selectedSize;

      if (!size) {
        this.addLabel.textContent = labelPlain;
        this.addButton.disabled = false;
        return;
      }

      if (!size.available) {
        this.addLabel.textContent = labelSoldOut;
        this.addButton.disabled = true;
        return;
      }

      let label = labelSize.replace(SIZE_PLACEHOLDER, size.value);

      if (this.equivalent) {
        label += ` (${labelEquivalent.replace(SIZE_PLACEHOLDER, this.equivalent)})`;
      }

      this.addLabel.textContent = label;
      this.addButton.disabled = false;
    }

    // "US 4" for a cell of the US row; null when the cell already reads like the product page size (38 or S for "38S")
    _equivalentForCell(cell, size) {
      if (!cell || !size) {
        return null;
      }

      const value = cell.textContent.trim();
      const isPageNotation = cell.hasAttribute('data-size-fr') || cell.hasAttribute('data-size-guide-primary');

      if (isPageNotation && normalizeSize(size.value).includes(normalizeSize(value))) {
        return null;
      }

      const row = cell.closest('tr')?.querySelector('th')?.textContent.trim();

      return row ? `${row} ${value}` : value;
    }

    // First row of the column written like the product page size: FR for "38S" or "38", INT for "S"
    _pageNotationRow(column, size) {
      const value = normalizeSize(size.value);

      return this.cells
        .filter((cell) => cell.dataset.sizeGuideCol === column)
        .filter((cell) => cell.hasAttribute('data-size-fr') || cell.hasAttribute('data-size-guide-primary'))
        .find((cell) => value.includes(normalizeSize(cell.textContent)))
        ?.closest(SELECTORS.row) || null;
    }

    // The reference row reads every size as on the product page ("38S"), and back to its own values when null
    _renderReferenceRow(row) {
      this.cells.forEach((cell) => {
        if (cell.dataset.originalText === undefined) {
          cell.dataset.originalText = cell.textContent;
        }

        const size = cell.closest(SELECTORS.row) === row ? this._sizeForColumn(cell.dataset.sizeGuideCol) : null;
        cell.textContent = size ? size.value : cell.dataset.originalText;
      });
    }

    // Only the given rows stay: the page size and the shopper's own sizing system; null shows every row
    _showRows(rows, reference = null) {
      this.rows.forEach((candidate) => {
        candidate.hidden = rows !== null && !rows.includes(candidate);
      });

      this._renderReferenceRow(reference);

      if (this.showAll) {
        this.showAll.hidden = rows === null;
      }
    }

    _renderUnits() {
      if (!this.measurements) {
        return;
      }

      const inches = this.unitSwitch.checked;
      this.unitSwitch.setAttribute('aria-checked', inches ? 'true' : 'false');
      this.measurements.innerHTML = inches ? this.measurementsHtml.replace(CM_PATTERN, cmToInches) : this.measurementsHtml;
    }

    /* ---------- Sync with the product form ---------- */

    _setSelectedSize(size, activeCell = null) {
      this.selectedSize = size;
      this.equivalent = this._equivalentForCell(activeCell, size);
      this._selectColumn(size ? this._columnForValue(size.value)?.column : null, activeCell);
      this._renderAddButton();
    }

    _syncFromForm() {
      const checked = document.querySelector(`input[type="radio"][name^="option"][form="${this.formId}"]:checked`);
      const size = checked ? this.sizes.find((item) => item.value === checked.value) : null;

      this._setSelectedSize(size || null);
    }

    _syncFromVariant(variant) {
      const options = variant?.options || [];
      const size = this.sizes.find((item) => options.includes(item.value));

      // Echo of a click in the table: keep the clicked cell and its equivalent
      if (size && size === this.selectedSize) {
        return;
      }

      this._showRows(null);
      this._setSelectedSize(size || null);
    }

    _onColumnClick(column, cell) {
      const size = this._sizeForColumn(column);

      if (!size) {
        return;
      }

      const radio = this._radioForSize(size);

      // Selecting the radio updates the product form, which answers with "variant:changed"
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.form?.dispatchEvent(new CustomEvent('size-guide:select', { detail: { variantId: size.variantId } }));

      const reference = this._pageNotationRow(column, size);

      this._setSelectedSize(size, cell);
      this._showRows([reference, cell.closest(SELECTORS.row)], reference);
    }

    /* ---------- Add to cart ---------- */

    _setBusy(busy) {
      if (!this.addButton) {
        return;
      }

      this.addButton.toggleAttribute('aria-busy', busy);
      this.addButton.disabled = busy || (this.selectedSize ? !this.selectedSize.available : false);
    }

    _addToCart() {
      if (!this.form || (this.selectedSize && !this.selectedSize.available)) {
        return;
      }

      this._setBusy(true);
      this.form.requestSubmit();
    }

    _close() {
      const container = this.closest('drawer-content, popover-content');

      if (container) {
        container.open = false;
      }

      // On mobile the guide is opened from the size selector sheet, which has to go away as well
      document.querySelector('.sizes-container .close-btn')?.click();
    }
  }

  window.customElements.define('size-guide', SizeGuide);
}
